import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router";
import apiAdmin from "../../../services/api-admin";
import { FilePath, FormatMoney } from '../../../services/utilities';
import { MdShoppingCart, MdDelete, MdInfoOutline } from "react-icons/md";
import { bootstrap } from "../../../app";
import "./merchone.scss";

const STORAGE_KEY = "merchOneCart";
const taxRate = 0.06;

export default function MerchOneHeader() {
    const [bucksOneBalance, setBucksOneBalance] = useState(0);
    const [isBucksOneLoading, setIsBucksOneLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const isCheckout = location.pathname.startsWith("/merchone-checkout");
    const infoModalRef = useRef(null);
    const cartOffcanvasRef = useRef(null);

    useEffect(() => {
        apiAdmin
            .get(`/get-bucksone-balance`)
            .then((response) => setBucksOneBalance(Number(response.data ?? 0)))
            .finally(() => setIsBucksOneLoading(false));
    }, []);

    const [cartItems, setCartItems] = useState(() => {
        if (typeof window === "undefined") return [];
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (!stored) return [];
        try {
            const parsed = JSON.parse(stored);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cartItems));
    }, [cartItems]);

    const cartItemCount = useMemo(
        () => cartItems.reduce((sum, item) => sum + Number(item.quantity ?? 0), 0),
        [cartItems]
    );

    const cartSubtotal = useMemo(
        () =>
            cartItems.reduce((sum, item) => {
                const qty = Number(item.quantity ?? 0);
                const base = Number(item.costPerItem ?? 0);
                const up = Number(item.sizeUpCharge ?? 0);
                return sum + (base + up) * qty;
            }, 0),
        [cartItems]
    );

    const openCart = useCallback(() => {
        if (!cartOffcanvasRef.current) return;
        const offcanvas = bootstrap.Offcanvas.getOrCreateInstance(cartOffcanvasRef.current);
        offcanvas.show();
    }, []);

    const closeCart = useCallback(() => {
        if (!cartOffcanvasRef.current) return;
        const offcanvas = bootstrap.Offcanvas.getInstance(cartOffcanvasRef.current);
        offcanvas?.hide();
    }, []);

    const removeFromCart = useCallback((itemToRemove) => {
        setCartItems((prev) =>
            prev.filter(
                (item) =>
                    !(
                        item.merchOneProductId === itemToRemove.merchOneProductId &&
                        item.size === itemToRemove.size &&
                        item.color === itemToRemove.color &&
                        item.companyLogo === itemToRemove.companyLogo
                    )
            )
        );
    }, []);

    const openInfoModal = () => {
        var infoModal = new bootstrap.Modal(infoModalRef.current, {});
        infoModal.show();
    }

    return (
        <>
            <nav className="merchone-nav">
                <div className="container-fluid py-2">
                    <div className="d-flex flex-column flex-sm-row align-items-sm-center w-100">
                        <div className="merchone-title">
                            <div className="fw-semibold">Important Store Information</div>
                            <button type="button" onClick={openInfoModal} className="btn btn-link p-0 info-button" aria-label="MerchOne information">
                                <MdInfoOutline size={18} />
                            </button>
                        </div>
                        <div className="merchone-header-actions ms-sm-auto">
                            <div className="bucksone">
                                <span>BucksOne Balance:</span>
                                {isBucksOneLoading ? (
                                    <span className="placeholder bg-light col-4 d-inline-block" />
                                ) : (
                                    <span className="amount">{FormatMoney(bucksOneBalance)}</span>
                                )}
                            </div>
                            <div className="header-action-right">
                                {isCheckout ? (
                                    <button type="button" className="btn btn-link p-0" onClick={() => navigate("/merchone")}>
                                        <strong>Keep shopping</strong>
                                    </button>
                                ) : (
                                    <button type="button" onClick={openCart} aria-controls="cartSummary" aria-label="Open cart" className="btn btn-link p-0 d-inline-flex align-items-center position-relative" id="cartButton">
                                        <MdShoppingCart size={25} />
                                        {cartItemCount > 0 && (
                                            <span className="cart-badge badge rounded-pill text-bg-dark">
                                                {cartItemCount}
                                                <span className="visually-hidden">items in cart</span>
                                            </span>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
            <div className="d-flex flex-column gap-2 mb-3">
                <div className="mb-2 d-flex align-items-center justify-content-center text-center">
                    <div className="d-flex flex-column">
                            <img src={FilePath('/images/admin/merchone-header.webp')} alt="MerchOne" className="img-fluid" />
                        </div>
                    </div>
                </div>
       
            <Outlet
                context={{
                    bucksOneBalance,
                    setBucksOneBalance,
                    isBucksOneLoading,
                    cartItems,
                    setCartItems,
                    removeFromCart,
                    openCart,
                    closeCart
                }}
            />
            <div className="offcanvas offcanvas-end" tabIndex="-1" id="cartSummary" aria-labelledby="cartSummaryLabel" data-bs-focus="false" ref={cartOffcanvasRef}>
                <div className="offcanvas-header">
                    <h5 className="offcanvas-title" id="cartSummaryLabel">
                        Cart Summary
                    </h5>
                    <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close" />
                </div>
                <div className="offcanvas-body">
                    <div className="d-flex flex-column gap-2">
                        {cartItems.length === 0 ? (
                            <div className="text-secondary">Your cart is empty.</div>
                        ) : (
                            cartItems.map((item) => (
                                <div
                                    key={`${item.productNumber ?? item.merchOneProductId}-${item.size}-${item.color}-${item.companyLogo}`}
                                    className="d-flex align-items-center gap-2"
                                >
                                    <div className="d-flex align-items-center justify-content-center flex-shrink-0 cart-image">
                                        <img src={item.imageUrl} alt={item.description} className="mw-100 mh-100 object-fit-contain" />
                                    </div>
                                    <div>
                                        <div className="fw-semibold">{item.description}</div>
                                        <div className="text-secondary">
                                            {FormatMoney(item.costPerItem)}
                                            {Number(item.sizeUpCharge ?? 0) > 0 && (
                                                <span> + {FormatMoney(item.sizeUpCharge)} (size)</span>
                                            )}
                                            {" "}· Qty {item.quantity}
                                        </div>
                                        <div className="text-secondary">{item.companyLogo}</div>
                                        <div className="text-secondary">
                                            Size: {item.size} · Color: {item.color}
                                        </div>
                                    </div>
                                    <button type="button" onClick={() => removeFromCart(item)} className="btn btn-link p-0 ms-auto" aria-label="Remove from cart">
                                        <MdDelete />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                    <hr className="my-2" />
                    <div className="d-flex align-items-center justify-content-between">
                        <div>Subtotal:</div>
                        <div>{FormatMoney(cartSubtotal)}</div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                        <div>Tax:</div>
                        <div>{FormatMoney(cartSubtotal * taxRate)}</div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between fw-bold">
                        <div>Total:</div>
                        <div>{FormatMoney(cartSubtotal * (1 + taxRate))}</div>
                    </div>
                    {bucksOneBalance > 0 &&
                        <div className="border-top mt-1 pt-1">
                            <div className="d-flex align-items-center justify-content-between fw-bold">
                                <div>BucksOne Balance:</div>
                                <div>{FormatMoney(bucksOneBalance)}</div>
                            </div>
                            {((cartSubtotal * (1 + taxRate)) > bucksOneBalance) &&
                                <div className="d-flex align-items-center justify-content-between fw-bold">
                                    <div>Balance Due After BucksOne:</div>
                                    <div>{FormatMoney((cartSubtotal * (1 + taxRate)) - bucksOneBalance)}</div>
                                </div>
                            }
                            {((cartSubtotal * (1 + taxRate)) <= bucksOneBalance) &&
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>BucksOne Remaining:</div>
                                    <div>{FormatMoney(bucksOneBalance - (cartSubtotal * (1 + taxRate)))}</div>
                                </div>
                            }
                        </div>
                    }
                    <div className="mt-3">
                        <button type="button" className="btn btn-primary w-100 rounded-3" onClick={() => { closeCart(); navigate("/merchone-checkout"); }} disabled={cartItems.length === 0} >
                            Checkout
                        </button>
                    </div>
                </div>
            </div>
            <div className="modal" tabIndex="-1" id="merchOneInfo" ref={infoModalRef}>
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">MerchOne Information</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <ul className="mb-0">
                                <li>All merchandise is custom embroidered with company logo upon ordering and cannot be returned.</li>
                                <li>Sales tax will be added to prices below. Sizes 2XL and larger will incur $1.50 per size above XL upcharge.</li>
                                <li><a href={FilePath('/documents/merchone-sizing-guide.pdf')} alt="MerchOne Sizing Guide" target="_blank" rel="noopener noreferrer">Go here to view sizing guide.</a></li>
                                <li>Not finding the perfect item? We can brand that, at no extra charge to you! Please view the Brand Your Own Clothes item description for instructions to get started.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}